import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section } from "../components/Section";
import { CTA } from "../components/CTA";
import { useIdentity } from "../lib/identity";
import { unifiedLogin, requestPasswordReset } from "../lib/unified-auth";
import { useTranslation } from "../lib/i18n";
import { Eye, EyeOff } from "lucide-react";

type Mode = "login" | "reset";

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setIdentity } = useIdentity();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError(t("login.form.errorEmail"));
      return;
    }
    setStatus("submitting");
    setError(null);

    if (mode === "login") {
      if (!password) {
        setError(t("login.form.errorPassword"));
        setStatus("idle");
        return;
      }
      const { identity, error: err } = await unifiedLogin({ email, password });
      if (err || !identity) {
        setError(err ?? "Login failed.");
        setStatus("error");
        return;
      }
      setIdentity(identity);
      setStatus("success");
      const target = identity.roles.includes("platform_admin") ? "/admin" : "/dashboard";
      setTimeout(() => navigate(target), 800);
    } else {
      const { error: err } = await requestPasswordReset(email);
      if (err) {
        setError(err);
        setStatus("error");
      } else {
        setStatus("success");
      }
    }
  };

  return (
    <>
      <SEO
        title="Login — Sentinel"
        description="Log in to your Sentinel account. One login works across the entire Sentinel Certification Platform."
        canonical="/login"
      />
      <PageHero
        eyebrow={t("login.page.eyebrow")}
        title={t("login.page.title")}
        description="One login. One identity. One session across Sentinel."
        bgVariant="slate"
        bgImage="https://images.pexels.com/photos/60504/security-protection-antivirus-software-40333.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      <Section>
        <div style={{ maxWidth: "440px", margin: "0 auto" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-muted)",
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: "pointer",
              marginBottom: "var(--space-3)",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.375rem",
            }}
          >
            &larr; Previous Page
          </button>
          {status === "success" ? (
            <div
              className="card"
              style={{
                borderColor: "var(--accent-400)",
                background: "var(--accent-50)",
                textAlign: "center",
                padding: "var(--space-6)",
              }}
            >
              <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>&#10003;</div>
              <h3 style={{ marginBottom: "0.5rem" }}>
                {mode === "login" ? t("login.success.login") : t("login.success.reset")}
              </h3>
              <p className="text-muted" style={{ marginBottom: "1rem" }}>
                {mode === "login"
                  ? t("login.success.loginBody")
                  : t("login.success.resetBody")}
              </p>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
                {mode === "login" ? (
                  <CTA to="/dashboard" variant="primary">Go to Dashboard</CTA>
                ) : (
                  <CTA to="/analytics" variant="primary">{t("cta.goToAnalytics")}</CTA>
                )}
                <button className="btn btn-secondary" onClick={() => setStatus("idle")}>
                  {t("login.back")}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: "0.5rem", marginBottom: "var(--space-4)" }}>
                <button
                  className="btn"
                  style={{
                    flex: 1,
                    background: mode === "login" ? "var(--accent-500)" : "var(--neutral-100)",
                    color: mode === "login" ? "var(--neutral-950)" : "var(--text-muted)",
                  }}
                  onClick={() => setMode("login")}
                >
                  {t("login.tab.login")}
                </button>
                <button
                  className="btn"
                  style={{
                    flex: 1,
                    background: mode === "reset" ? "var(--accent-500)" : "var(--neutral-100)",
                    color: mode === "reset" ? "var(--neutral-950)" : "var(--text-muted)",
                  }}
                  onClick={() => setMode("reset")}
                >
                  {t("login.tab.reset")}
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="email">{t("login.form.email")}</label>
                  <input
                    id="email"
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@organization.com"
                  />
                </div>
                {mode === "login" && (
                  <div className="form-group">
                    <label className="form-label" htmlFor="password">{t("login.form.password")}</label>
                    <div style={{ position: "relative" }}>
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        className="form-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t("login.form.passwordPlaceholder")}
                        style={{ paddingRight: "2.5rem" }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: "absolute",
                          right: "0.625rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--text-muted)",
                          padding: 0,
                        }}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                )}
                {error && <p className="form-error">{error}</p>}
                <button
                  type="submit"
                  className="btn btn-primary btn-lg btn-block"
                  disabled={status === "submitting"}
                >
                  {status === "submitting"
                    ? t("login.form.waiting")
                    : mode === "login"
                      ? t("login.form.logIn")
                      : t("login.form.sendReset")}
                </button>
              </form>

              <div style={{ marginTop: "var(--space-4)", textAlign: "center" }}>
                <p className="text-muted" style={{ fontSize: "0.9375rem" }}>
                  {t("login.noAccount")}{" "}
                  <CTA to="/register" variant="secondary" >
                    {t("cta.registerHere")}
                  </CTA>
                </p>
              </div>

              <div style={{ marginTop: "var(--space-4)" }}>
                <div
                  className="card"
                  style={{ padding: "var(--space-3)", background: "var(--neutral-50)" }}
                >
                  <h4 style={{ fontSize: "0.875rem", fontWeight: 700, marginBottom: "0.5rem" }}>
                    {t("login.features.title")}
                  </h4>
                  <ul style={{ display: "flex", flexDirection: "column", gap: "0.375rem" }}>
                    {[
                      t("login.feature1"),
                      t("login.feature2"),
                      t("login.feature3"),
                      t("login.feature4"),
                    ].map((f) => (
                      <li key={f} style={{ fontSize: "0.875rem", display: "flex", gap: "0.5rem" }}>
                        <span style={{ color: "var(--accent-500)", flexShrink: 0 }}>&#10003;</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>


            </>
          )}
        </div>
      </Section>
    </>
  );
}
